import { getLogger, httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import type {
  Biller,
  BillerAccount,
  EnrollResponse,
  SearchResponse,
  SubmitRequest,
  ValidateRequest,
  ValidateResponse,
} from './billPresentmentTypes';

const log = getLogger('flux/bill-presentment/api.ts');

export default {
  async search(query: string) {
    try {
      const requestUrl = `${getServiceUrl()}/malipo-biller-directory/api/v2/biller/search`;
      const response = (await httpClientInterface.send('POST', requestUrl, {
        intent: 'PRESENTMENT',
        billerName: query,
      })) as { data: SearchResponse };
      log.debug('biller searchResponse', response?.data);
      const filteredBillers = response?.data?.data?.filter((datum) => datum.mode === 'PARTNER') || [];
      log.debug('biller searchResponse filtered', filteredBillers);
      response.data.data = filteredBillers;
      return response?.data as SearchResponse;
    } catch (e) {
      log.warn('BPS search error!', e);
      return { data: [] };
    }
  },
  async getPopularBillers() {
    const requestUrl = `${getServiceUrl()}/malipo-biller-directory/api/v1/biller/intent/PRESENTMENT/popular`;
    const response = (await httpClientInterface.send<{ data: Biller[] }, any, any>(
      'GET',
      requestUrl,
      undefined,
      undefined,
      {
        // We want the limit as small as possible because responses aren't sorted by popularity, but alphabetically
        limit: 32, // 32 filters down to a perfect 16
      },
    )) as {
      data: { data: Biller[] };
    };

    const filteredBillers = response?.data?.data?.filter((datum) => datum.mode === 'PARTNER') || [];
    log.debug('biller getPopularBillersResponse filtered', filteredBillers);
    return filteredBillers;
  },
  async getEnrollment(id: string) {
    const requestUrl = `${getServiceUrl()}/malipo-biller-directory/api/v1/biller/${id}/enrollment?exclude-hidden=true`;
    const response = await httpClientInterface.send('GET', requestUrl);
    log.debug('biller enrollResponse', response?.data);
    return response?.data as EnrollResponse;
  },
  async validate(id: string, payload: ValidateRequest) {
    const requestUrl = `${getServiceUrl()}/malipo-biller-directory/api/v1/biller/${id}/enrollment/validate`;
    const response = await httpClientInterface.send('POST', requestUrl, payload);
    log.debug('billerAccount validateResponse', response?.data);
    return response?.data as ValidateResponse;
  },
  async checkConflicts(billerId: string, accountIdentifier: string) {
    const requestUrl = `${getServiceUrl()}/aikya-presentment/api/v1/biller-account/search-by-account-identifier`;
    const response = await httpClientInterface.send('POST', requestUrl, { billerId, accountIdentifier });
    log.debug('billerAccount checkConflicts response', response?.data);
    return response?.data as BillerAccount;
  },
  async submit(payload: SubmitRequest) {
    const requestUrl = `${getServiceUrl()}/aikya-presentment/api/v2/biller-account/submit`;
    const response = await httpClientInterface.send('POST', requestUrl, payload);
    return response?.data as BillerAccount;
  },
  async confirm(id: string) {
    const requestUrl = `${getServiceUrl()}/aikya-presentment/api/v2/biller-account/${id}/confirm`;
    const response = await httpClientInterface.send('PUT', requestUrl);
    return response?.data as BillerAccount;
  },

  async fetch() {
    const requestUrl = `${getServiceUrl()}/aikya-presentment/api/v1/biller-account`;
    const response = (await httpClientInterface.send<{ data: BillerAccount[] }, any, any>('GET', requestUrl)) as {
      data: { data: BillerAccount[] };
    };
    log.debug('billerAccount fetch Response', response?.data);
    return response?.data?.data; // ugly but I didn't make the APIs
  },
  async getBillerAccount(id: string) {
    const requestUrl = `${getServiceUrl()}/aikya-presentment/api/v1/biller-account/${id}`;
    const response = await httpClientInterface.send<{ data: BillerAccount[] }, any, any>('GET', requestUrl);
    log.debug('get billerAccount response', response?.data);
    return response?.data as BillerAccount;
  },
  async delete(id: string) {
    const requestUrl = `${getServiceUrl()}/aikya-presentment/api/v1/biller-account/${id}`;
    await httpClientInterface.send('DELETE', requestUrl);
  },

  async getBiller(id: string) {
    const requestUrl = `${getServiceUrl()}/malipo-biller-directory/api/v2/biller/${id}`;
    const response = await httpClientInterface.send('GET', requestUrl);
    return response?.data as Biller;
  },
  async getBillers(ids: string[]) {
    // get billers in parallel and collapse to one array so we only have one reducer hit
    const billers: Biller[] = await Promise.all(
      ids.map(async (id) => {
        const requestUrl = `${getServiceUrl()}/malipo-biller-directory/api/v2/biller/${id}`;
        const response = await httpClientInterface.send('GET', requestUrl);
        return response.data as Biller;
      }),
    );
    return billers;
  },
} as const;
